/**
 * HubShell — the shared chrome for every tab in Broker Intelligence Hub.
 * Renders: nav bar → stats strip → tab bar → body slot (active tab content).
 * Mirrors /pages/admin/ContactsPage layout exactly in navy+gold.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faHouse, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { colors, fonts, FONT_LINK_HREF, gradients } from './colors';

export interface HubMetric {
  icon: IconDefinition;
  value: string | number;
  label: string;
  color?: string; // defaults to colors.gold2
}

export interface HubTab {
  key: string;
  label: string;
  icon: IconDefinition;
  badge?: number | string | null;
}

/**
 * The gold-highlighted word inside the brand logo. We render the brand as
 *   AI<gold>Middle</gold>Portal
 * so each hub can set its own identity without duplicating the nav chrome.
 *   - Broker Intelligence → "Broker"     (AIBrokerPortal)
 *   - Agent Portal        → "Agent"      (AIAgentPortal)
 *   - Future/default      → "Search"     (AISearchPortal, global brand)
 */
interface HubShellProps {
  breadcrumb: string;          // e.g. "Broker Intelligence / Dashboard"
  brandMiddle?: string;        // gold-highlighted middle word (default: "Broker")
  metrics?: HubMetric[];       // stats strip metrics (can be empty)
  actions?: React.ReactNode;   // right-aligned buttons in stats strip
  tabs: HubTab[];
  activeTab: string;
  onTabChange: (key: string) => void;
  children: React.ReactNode;   // active tab body
  rightNavContent?: React.ReactNode; // optional: extra nav links rendered BEFORE Home/Sign Out
  // Top-nav escape hatches. Both render as default nav buttons on the far
  // right of the nav bar so the user can always get out of the hub.
  homeHref?: string;           // defaults to "/"
  onSignOut?: () => void;      // defaults to clearing auth + redirecting to /auth/login
}

// Shared default sign-out: same clear-and-redirect pattern used by
// AgentDashboardModern so all hubs get consistent logout behavior.
// Redirects to /agent-portal/login — the working login page. The old
// /auth/login route responded with "Access Error: Agent not found".
const defaultSignOut = () => {
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_info');
    localStorage.removeItem('brokerageId');
  } catch { /* ignore */ }
  document.cookie = 'isLoggedInKIQ=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  window.location.href = '/agent-portal/login';
};

const HubShell: React.FC<HubShellProps> = ({
  breadcrumb,
  brandMiddle = 'Broker',
  metrics = [],
  actions,
  tabs,
  activeTab,
  onTabChange,
  children,
  rightNavContent,
  homeHref = '/',
  onSignOut = defaultSignOut,
}) => {
  return (
    // Constrain the hub to the viewport exactly so the nav + stats strip +
    // tab bar stay fixed in place while the body below them scrolls
    // independently. `height: 100vh` + `overflow: hidden` is load-bearing:
    // without it, the whole page grows and scrolls together, which loses
    // the fixed-header UX the broker needs on the long tabs.
    <div
      style={{
        background: colors.navy,
        height: '100vh',
        overflow: 'hidden',
        fontFamily: fonts.body,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <link href={FONT_LINK_HREF} rel="stylesheet" />

      {/* Nav Bar — brand is AI Broker Portal (distinct from the global
          AISearchPortal brand used by agent/customer portals). */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 0', flexShrink: 0 }}>
        <div style={{ padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: fonts.display, fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>{brandMiddle}</span>Portal
              </span>
            </a>
            <span style={{ color: colors.muted, fontSize: '14px', marginLeft: '16px' }}>
              / {breadcrumb}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {rightNavContent}

            {/* Dashboard — navigates to the caller-specified home route
                (e.g. /agent-portal, /admin) so users can always bail out
                of the hub back to their dashboard. */}
            <a
              href={homeHref}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                color: colors.white,
                border: `1px solid ${colors.navy3}`,
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontFamily: fonts.body,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.navy3;
                e.currentTarget.style.borderColor = colors.gold;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = colors.navy3;
              }}
              title="Back to dashboard"
            >
              <FontAwesomeIcon icon={faHouse} style={{ fontSize: '13px' }} />
              Dashboard
            </a>

            {/* Sign Out — clears auth state and sends to /auth/login.
                Default handler can be overridden via onSignOut prop. */}
            <button
              type="button"
              onClick={onSignOut}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                color: colors.gold2,
                border: `1px solid ${colors.gold}`,
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontFamily: fonts.body,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.gold;
                e.currentTarget.style.color = colors.navy;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.gold2;
              }}
              title="Sign out"
            >
              <FontAwesomeIcon icon={faRightFromBracket} style={{ fontSize: '13px' }} />
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      {/* Stats Strip — only rendered if metrics OR actions are provided */}
      {(metrics.length > 0 || actions) && (
        <div
          style={{
            background: colors.navy2,
            borderBottom: `1px solid ${colors.navy3}`,
            padding: '16px 24px',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
              {metrics.map((m, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FontAwesomeIcon icon={m.icon} style={{ color: m.color || colors.gold2 }} />
                  <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{m.value}</span>
                  <span style={{ color: colors.muted, fontSize: '12px' }}>{m.label}</span>
                </div>
              ))}
            </div>
            {actions && <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>{actions}</div>}
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div
        style={{
          background: colors.navy2,
          borderBottom: `1px solid ${colors.navy3}`,
          flexShrink: 0,
          overflowX: 'auto',
        }}
      >
        <div style={{ display: 'flex', padding: '0 16px' }}>
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: `3px solid ${isActive ? colors.gold : 'transparent'}`,
                  color: isActive ? colors.gold2 : colors.muted,
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s, border-bottom-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = colors.white;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = colors.muted;
                }}
              >
                <FontAwesomeIcon icon={tab.icon} style={{ fontSize: '13px' }} />
                <span>{tab.label}</span>
                {tab.badge != null && tab.badge !== '' && (
                  <span
                    style={{
                      background: isActive ? colors.gold : colors.navy3,
                      color: isActive ? colors.navy : colors.muted,
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: '10px',
                      marginLeft: '2px',
                    }}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab body — fills remaining viewport */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</div>
    </div>
  );
};

/** Reusable primary button in gold gradient — matches stats-strip action style. */
export const GoldButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  style,
  ...rest
}) => (
  <button
    {...rest}
    style={{
      background: gradients.goldButton,
      border: 'none',
      borderRadius: '6px',
      padding: '8px 14px',
      fontSize: '12px',
      fontWeight: 600,
      color: colors.navy,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      ...style,
    }}
  >
    {children}
  </button>
);

/** Outline secondary button — matches stats-strip secondary action style. */
export const OutlineButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  style,
  ...rest
}) => (
  <button
    {...rest}
    style={{
      background: 'transparent',
      border: `1px solid ${colors.muted}`,
      borderRadius: '6px',
      padding: '8px 14px',
      fontSize: '12px',
      fontWeight: 500,
      color: colors.muted,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      ...style,
    }}
  >
    {children}
  </button>
);

export default HubShell;
